/** epoch ms → "HH:MM" (e.g. 14:30) */
export function formatTimeFromMs(ms: number): string {
  const date = new Date(ms);
  const h = date.getHours();
  const m = date.getMinutes();
  return `${h}:${m.toString().padStart(2, "0")}`;
}

/** 날짜 차이를 한글 레이블로 변환 (뒤에 공백 포함) */
function dayDiffLabel(diff: number): string {
  if (diff === 0) return "";
  if (diff === 1) return "익일 ";
  if (diff === -1) return "전일 ";
  if (diff > 1) return `${diff}일후 `;
  return `${Math.abs(diff)}일전 `;
}

/** 날짜 차이를 짧은 태그로 변환 (공백 없음) */
function dayDiffTag(diff: number): string {
  if (diff === 0) return "";
  if (diff === 1) return "익일";
  if (diff === -1) return "전일";
  if (diff > 1) return `${diff}일후`;
  return `${Math.abs(diff)}일전`;
}

/** 뷰포트 기준 날짜 차이 계산 */
function calcDayDiffs(startMs: number, endMs: number, activeDayMs?: number) {
  const startDate = new Date(startMs);
  const endDate = new Date(endMs);

  const refDay = activeDayMs != null
    ? new Date(activeDayMs)
    : new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());

  const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return {
    startDiff: Math.round((startDay.getTime() - refDay.getTime()) / MS_PER_DAY),
    endDiff: Math.round((endDay.getTime() - refDay.getTime()) / MS_PER_DAY),
  };
}

/**
 * epoch ms pair → 시간 범위 문자열 (뷰포트 날짜 기준)
 *
 * 같은 날: "14:30-16:00"
 * 익일 넘김: "23:30~익일 1:15"
 * 전일 시작: "전일 23:30~1:15"
 * N일 차이: "2일전 23:30~5:35"
 * 양쪽 넘김: "전일 23:30~3일후 5:35"
 */
export function formatTimeRangeFromMs(
  startMs: number,
  endMs: number,
  activeDayMs?: number,
): string {
  const { startDiff, endDiff } = calcDayDiffs(startMs, endMs, activeDayMs);
  const startTime = formatTimeFromMs(startMs);
  const endTime = formatTimeFromMs(endMs);

  if (startDiff < 0 && endDiff > 0) {
    return `${dayDiffLabel(startDiff)}${startTime}~${dayDiffLabel(endDiff)}${endTime}`;
  }
  if (startDiff < 0) return `${dayDiffLabel(startDiff)}${startTime}~${endTime}`;
  if (endDiff > 0) return `${startTime}~${dayDiffLabel(endDiff)}${endTime}`;
  return `${startTime}-${endTime}`;
}

/**
 * 사이드바용 컴팩트 시간 범위
 *
 * 같은 날: { display: "14:30-16:00", tooltip: null }
 * 자정 넘김: { display: "~익일", tooltip: "23:30~익일 1:15" }
 * 양쪽 넘김: { display: "전일~3일후", tooltip: "전일 23:30~3일후 5:35" }
 */
export function formatTimeRangeCompact(
  startMs: number,
  endMs: number,
  activeDayMs: number,
): { display: string; tooltip: string | null } {
  const { startDiff, endDiff } = calcDayDiffs(startMs, endMs, activeDayMs);

  // 같은 날 — 인라인 풀 표시
  if (startDiff === 0 && endDiff === 0) {
    return {
      display: `${formatTimeFromMs(startMs)}-${formatTimeFromMs(endMs)}`,
      tooltip: null,
    };
  }

  // 자정 넘김 — 짧은 태그 인라인, 풀 시간은 툴팁
  const full = formatTimeRangeFromMs(startMs, endMs, activeDayMs);

  const startTag = startDiff < 0 ? dayDiffTag(startDiff) : "";
  const endTag = endDiff > 0 ? dayDiffTag(endDiff) : "";

  let compact: string;
  if (startTag && endTag) {
    compact = `${startTag}~${endTag}`;
  } else if (startTag) {
    compact = `${startTag}~`;
  } else {
    compact = `~${endTag}`;
  }

  return { display: compact, tooltip: full };
}
